import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import pettyCashApi from '../../services/pettyCashApi';
import type { AuxiliaryList, CashAccountOption, HandoverInput, PettyCashHolder } from '../../types/pettyCash';
import { amountInput, formatAmount, parseOptionalAmount } from '../../utils/pettyCash';

/**
 * Starts a handover of a holder's petty cash to a successor: who takes it over, the SAP cash account and auxiliary
 * (as in the holder dialog; the outgoing holder's by default), the float and a note. Both then confirm from Petty Cash
 * › Claims: the outgoing holder counts the cash, then the successor takes it over, which completes it.
 */

interface Props {
  open: boolean;
  companyId: number;
  currency: string;
  /** The outgoing holder. */
  holder: PettyCashHolder | null;
  holders: PettyCashHolder[];
  users: Array<{ userId: number; name: string; email: string | null }>;
  onClose: () => void;
  onSave: (input: HandoverInput) => Promise<void>;
}

type AuxOption = { code: string; name: string };

const HandoverDialog: React.FC<Props> = ({ open, companyId, currency, holder, holders, users, onClose, onSave }) => {
  const [toUserId, setToUserId] = useState<number | ''>('');
  const [account, setAccount] = useState('');
  const [auxCode, setAuxCode] = useState<string | null>(null);
  const [floatText, setFloatText] = useState('');
  const [note, setNote] = useState('');
  const [accounts, setAccounts] = useState<CashAccountOption[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [aux, setAux] = useState<AuxiliaryList | null>(null);
  const [auxLoading, setAuxLoading] = useState(false);
  const [auxError, setAuxError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !holder) return;
    setToUserId('');
    setAccount(holder.sapAccount);
    setAuxCode(holder.auxiliaryCode ?? null);
    setFloatText(amountInput(holder.floatAmount));
    setNote('');
    setError(null);
    setBusy(false);
    if (!accounts) {
      pettyCashApi.cashAccounts(companyId)
        .then(setAccounts)
        .catch((e: any) => { setAccounts([]); setAccountsError(e?.response?.data?.message || 'The SAP cash accounts could not be read'); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, holder]);

  const chosen = accounts?.find((a) => a.code === account) ?? null;
  // The outgoing holder's own account counts as taking an auxiliary when he has one, even before SAP's list is read.
  const takesAux = (!!chosen && (!!chosen.auxType || !!chosen.auxMandatory)) || (!chosen && !!auxCode);

  useEffect(() => {
    if (!open || !account || !takesAux) { setAux(null); setAuxError(null); return undefined; }
    let cancelled = false;
    setAuxLoading(true);
    setAuxError(null);
    pettyCashApi.auxiliaries(companyId, { account })
      .then((list) => { if (!cancelled) setAux(list); })
      .catch((e: any) => { if (!cancelled) { setAux(null); setAuxError(e?.response?.data?.message || 'The auxiliaries could not be read from SAP'); } })
      .finally(() => { if (!cancelled) setAuxLoading(false); });
    return () => { cancelled = true; };
  }, [open, companyId, account, takesAux]);

  if (!holder) return null;

  const activeHolders = new Map(holders.filter((h) => h.isActive).map((h) => [h.userId, h]));
  const successor = toUserId ? activeHolders.get(Number(toUserId)) : undefined;
  // Codes other active holders of the account use (the outgoing holder's own code passes to the successor).
  const usedBy = new Map(
    holders
      .filter((h) => h.isActive && h.holderId !== holder.holderId && h.userId !== Number(toUserId) && h.sapAccount === account && h.auxiliaryCode)
      .map((h) => [h.auxiliaryCode as string, h.userName]),
  );
  const auxOptions: AuxOption[] = aux?.codes ?? [];
  const auxValue: AuxOption | null = auxCode
    ? auxOptions.find((o) => o.code === auxCode) ?? { code: auxCode, name: holder.auxiliaryCode === auxCode ? holder.auxiliaryName || '' : '' }
    : null;
  const typeLabel = aux?.typeName ? `${aux.typeName}${aux.type && aux.type !== aux.typeName ? ` (${aux.type})` : ''}` : chosen?.auxType || '';

  const save = async () => {
    if (!toUserId) { setError('Choose who takes the petty cash over.'); return; }
    if (!account) { setError('Choose the SAP cash account.'); return; }
    if (chosen?.auxMandatory && !auxCode) { setError(`Choose the auxiliary: ${account} needs one.`); return; }
    if (auxCode && usedBy.has(auxCode)) { setError(`${auxCode} is already ${usedBy.get(auxCode)}'s auxiliary on ${account}.`); return; }
    const amount = parseOptionalAmount(floatText);
    if (!amount.ok) { setError('The float is not an amount.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave({
        companyId,
        fromUserId: holder.userId,
        toUserId: Number(toUserId),
        sapAccount: account,
        // As the holder dialog: the code chosen (cleared when the account changes).
        auxiliaryCode: auxCode,
        floatAmount: amount.value,
        note: note.trim() || null,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The handover could not be started');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="pc-handover-title">
      <DialogTitle id="pc-handover-title">Hand over the petty cash of {holder.userName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {holder.userName} counts the cash and confirms it, then the successor confirms receiving it (both from Petty Cash › Claims).
            {` ${holder.userName}`} then stops being a holder, and the successor holds the petty cash with the account, auxiliary and float below.
          </Typography>
          <Typography variant="body2">
            Now: {holder.sapAccount} {holder.sapAccountName || ''}{holder.auxiliaryCode ? ` · auxiliary ${holder.auxiliaryCode}` : ''}
            {holder.floatAmount ? ` · float ${formatAmount(holder.floatAmount)} ${currency}` : ''}
            {holder.outstanding !== undefined ? ` · claims not yet reimbursed ${formatAmount(holder.outstanding)}` : ''}
          </Typography>
          <TextField
            id="pc-handover-to"
            select
            label="Successor"
            value={toUserId}
            onChange={(e) => setToUserId(Number(e.target.value) || '')}
            required
            fullWidth
            helperText="Users who can open this company"
          >
            {users.filter((u) => u.userId !== holder.userId).map((u) => (
              <MenuItem key={u.userId} value={u.userId}>
                {u.name}{u.email ? ` · ${u.email}` : ''}{activeHolders.has(u.userId) ? ' (already a holder)' : ''}
              </MenuItem>
            ))}
          </TextField>
          {successor ? (
            <Alert severity="warning">
              {successor.userName} is already a holder ({successor.sapAccount}{successor.auxiliaryCode ? ` · ${successor.auxiliaryCode}` : ''}): the handover gives
              {' '}{successor.userName} this account, auxiliary and float instead.
            </Alert>
          ) : null}
          {accounts === null ? (
            <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Reading the SAP cash accounts…</Typography>
          ) : (
            <TextField
              id="pc-handover-account"
              select
              label="SAP petty cash account"
              value={account}
              onChange={(e) => { setAccount(e.target.value); setAuxCode(null); }}
              required
              fullWidth
              error={!!accountsError}
              helperText={accountsError || 'Usually the outgoing holder\'s account'}
            >
              {account && !accounts.some((a) => a.code === account) ? <MenuItem value={account}>{account} {holder.sapAccount === account ? holder.sapAccountName || '' : ''}</MenuItem> : null}
              {accounts.map((a) => (
                <MenuItem key={a.code} value={a.code}>
                  {a.code} {a.name}
                  {a.auxType ? ` · auxiliaries ${a.auxType}` : ''}
                  {a.balance !== null ? ` · balance ${formatAmount(a.balance)} ${currency}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {takesAux ? (
            auxLoading ? (
              <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Reading the auxiliaries from SAP…</Typography>
            ) : (
              <Autocomplete
                id="pc-handover-aux"
                options={auxOptions}
                value={auxValue}
                onChange={(_e, v) => setAuxCode(v?.code ?? null)}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
                getOptionDisabled={(o) => usedBy.has(o.code)}
                renderOption={(props, o) => (
                  <li {...props} key={o.code}>
                    {o.code} {o.name}{usedBy.has(o.code) ? ` · ${usedBy.get(o.code)}` : ''}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Auxiliary (Dimension 5)${typeLabel ? `: ${typeLabel}` : ''}`}
                    required={!!chosen?.auxMandatory}
                    error={!!auxError}
                    helperText={auxError || 'The successor\'s code on the shared cash account; usually the outgoing holder\'s'}
                  />
                )}
              />
            )
          ) : null}
          <TextField
            id="pc-handover-float"
            label={`Float ${currency}`.trim()}
            value={floatText}
            onChange={(e) => setFloatText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 20))}
            inputProps={{ inputMode: 'decimal' }}
            helperText="The successor's float (optional)"
            fullWidth
          />
          <TextField id="pc-handover-note-start" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} multiline minRows={2} fullWidth />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy}>{busy ? 'Starting…' : 'Start the handover'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default HandoverDialog;
